import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import './home.css';
import LinkMenu from "../linkMenu/linkMenu";
import { useDispatch, useSelector } from "react-redux";
import app from '../';
import { Navigate, Route, Routes } from "react-router-dom";
import { FullWindowSpinner, MonkeyNotFound } from "../../shared";
import drive, { DriveContextualMenu, MyDrive } from "../../drive";
import users from "../../users";
import { isMobileDevice, setDarkMode } from "../../../utils/utils";
import { FormattedMessage } from "react-intl";
import FileUploadMenu from "../../drive/driveContextualMenu/fileUploadMenu/FileUploadMenu";
import groupIndex, { GroupDetails, GroupForm, GroupResults, GroupSearcher } from "../../group";
import auth from "../../../utils/authentication";
import SharedFile from "../../public/sharedFile/sharedFile";
import LoginPage from "../../users/loginPage/loginPage";
import HomeFeed from "../homeFeed/homeFeed";
import database from "../../../utils/databaseFirebase";
import Cap17driveIndex from "../../cap17drive/cap17driveIndex/cap17driveIndex";
import ChatbotHome from "../../chatbot/chatbotHome/chatbotHome";
import ChatbotSidebar from "../../chatbot/chatbotSidebar/chatbotSidebar";
import { useChatStore } from '../../chatbot/store/chatbotStore';
import Cap17driveSearcher from "../../cap17drive/cap17driveSearcher/cap17driveSearcher";
import SquadflixTvControls from "../../cap17drive/SquadflixTvControls/squadflixTvControls";
import useSquadflixTv from "../../../hooks/useSquadflixTv";
import MyProfileView from "../myProfileView/myProfileView";
import SquadflixLogsView from "../../cap17drive/squadflixLogsView/squadflixLogsView";
import ChatbotUsers from "../../chatbot/chatbotUsers/chatbotUsers";
import DownloadQueue from "../../drive/downloadQueue/downloadQueue";
import useDownloadStore from "../../../store/downloadStore";
import UseUserPreferences from '../../user-app-config/hooks/use-user-preferences';
import UserProfileDetails from "../user-profile-details/user-profile-details";
import { ogFunctions } from "../../../apis/og-functions/ogFunctionsApi";
import SquadflixChatWidget from "../squadflix-chat/SquadflixChatWidget";
import TvModePromptModal from "../tv-mode-prompt-modal/TvModePromptModal";
import TvBasicLayout from "../tv-basic-layout/TvBasicLayout";

const showTvPrompModal = false;

const Home = () => {

    const loading = useSelector(app.selectors.isLoading);
    const dispatch = useDispatch();

    const userGroups = useSelector(groupIndex.selectors.userGroups);

    const [isOpen, setIsOpen] = useState(false);
    const [isDraggingItem, setIsDraggingItem] = useState(false);
    const buttonToggleContextual = useRef(null);
    const [timeoutStopDragging, setTimeoutStopDragging] = useState(null);
    const isUploading = useSelector(drive.selectors.isUploading);
    const squadflixTvHook = useSquadflixTv();
    const { state } = useDownloadStore(state => state);
    const { currentFile: downloadingFile } = state;
    const { userPreferences, loading: loadingPreferences } = UseUserPreferences();

    const {
        hasPermission
    } = useChatStore();

    useEffect(() => {
        if (loading || !auth.currentUser) {
            return;
        }

        const getCurrentSize = ogFunctions.httpsCallable('getCurrentSize');

        getCurrentSize().then(sizeData => {
            dispatch(users.actions.loadedUserSizeData(sizeData.data));
        })

        const getTrashFiles = ogFunctions.httpsCallable('getTrashFiles');

        getTrashFiles().then(res => {
            dispatch(drive.actions.setTrashFiles([...res.data.folders, ...res.data.files]));
        })

        const getUserGroups = ogFunctions.httpsCallable("getFullUserGroups");

        getUserGroups().then(res => {
            dispatch(groupIndex.actions.loadedGroups(res.data.groups, res.data.askedGroups, res.data.groupInvites));
        })

        const getUserConfiguration = ogFunctions.httpsCallable('getUserConfiguration');
        getUserConfiguration().then(res => {
            dispatch(users.actions.loadedUserConfig(res.data));
            setDarkMode(res.data.darkMode);
        })

    }, [dispatch, loading]);

    useEffect(() => {
        const groupRefs = userGroups?.groups.map(async group => {
            if (group && !group.tags) {
                const groupTagsRef = database.ref('/groupTags/')
                    .orderByChild('groupId')
                    .equalTo(group.id);
                const tags = [];
                await groupTagsRef.once('value').then(snapshots => {

                    snapshots.forEach(snapshot => {
                        const newRole = snapshot.val();
                        newRole.id = snapshot.key;
                        tags.push(newRole);
                    })
                    group.tags = tags;
                    dispatch(groupIndex.actions.updateLoadedGroup(group));
                    // Set up a listener for changes
                    groupTagsRef.on('child_added', snapshot => {
                        if (tags.some(x => x.id === snapshot.key)) {
                            return;
                        }
                        const newRole = snapshot.val();
                        newRole.id = snapshot.key;
                        if (tags.some(x => x.id === newRole.id)) {
                            return;
                        }
                        tags.push(newRole);
                        tags.sort((a, b) => a.tag.localeCompare(b.tag));
                        group.tags = tags;
                        dispatch(groupIndex.actions.updateLoadedGroup(group));
                    });
                    groupTagsRef.on('child_changed', snapshot => {
                        const newRole = snapshot.val();
                        newRole.id = snapshot.key;
                        const editedRoleIndex = tags.findIndex(x => x.id === newRole.id);
                        tags.splice(editedRoleIndex, 1);
                        tags.push(newRole);
                        tags.sort((a, b) => a.tag.localeCompare(b.tag));
                        group.tags = tags;
                        dispatch(groupIndex.actions.updateLoadedGroup(group));
                    });
                    groupTagsRef.on('child_removed', snapshot => {
                        const deletedRoleIndex = tags.findIndex(x => x.id === snapshot.key);
                        tags.splice(deletedRoleIndex, 1);
                        group.tags = tags;
                        dispatch(groupIndex.actions.updateLoadedGroup(group));
                    });
                })
                return groupTagsRef;

            }
            return null;
        });
        return () => {
            if (!groupRefs) {
                return;
            }
            Promise.all(groupRefs).then(refs => {
                if (refs && refs.length > 0) {
                    refs?.forEach(ref => {
                        if (ref) {
                            ref.off('child_added')
                            ref.off('child_changed');
                            ref.off('child_removed');
                        }
                    })
                }
            });
        }
    }, [dispatch, userGroups?.groups])

    const [groupsSubscribed, setGroupSubscribed] = useState([]);

    useEffect(() => {
        if (!userGroups?.groups) {
            return;
        }

        const groupIds = userGroups.groups.map(x => x.id);

        groupIds.forEach(async groupId => {
            if (groupsSubscribed.includes(groupId)) {
                return;
            }
            groupsSubscribed.push(groupId);
            const groupFilesRef = database.ref('/groupFile').orderByChild('groupId').equalTo(groupId);
            const posts = [];
            await groupFilesRef.once('value').then(res => {
                res.forEach(snapshot => {
                    const post = snapshot.val();
                    post.id = snapshot.key;
                    post.uploadDate = new Date(post.file.uploadDate);
                    posts.push(post);
                });

                dispatch(app.actions.addGroup({ groupId: groupId, posts: posts.reverse() }));

                // Set up a listener for changes
                groupFilesRef.on('child_added', snapshot => {
                    if (posts.some(x => x.id === snapshot.key)) {
                        return;
                    }
                    const newFile = snapshot.val();
                    newFile.uploadDate = new Date(newFile.file.uploadDate);
                    newFile.id = snapshot.key;
                    dispatch(app.actions.addPost(newFile));
                });
                groupFilesRef.on('child_changed', snapshot => {
                    const newFile = snapshot.val();
                    newFile.id = snapshot.key;
                    dispatch(app.actions.modifyPost(newFile));
                });
                groupFilesRef.on('child_removed', snapshot => {
                    const newFile = snapshot.val();
                    newFile.id = snapshot.key;
                    dispatch(app.actions.removePost(newFile));
                });
            });
        });
    }, [userGroups?.groups]);

    const homeThing = useMemo(() => {
        if (loadingPreferences) {
            return <FullWindowSpinner />;
        }
        if (userPreferences.home === 'home') {
            return <HomeFeed />;
        } else if (userPreferences.home === 'squadflix') {
            return <Navigate to="/squadflix/" replace />;
        } else if (userPreferences.home === 'drive') {
            return <Navigate to="/my-drive" replace />;
        }
    }, [loadingPreferences, userPreferences]);

    const isOpenInIframe = useMemo(() => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }, []);

    if (loading) {
        return <FullWindowSpinner />;
    }

    function toggleContextualMenu() {
        setIsOpen(!isOpen);
        buttonToggleContextual.current.click();
    }

    const mainRoutes = (

        <Routes>
            {auth.currentUser &&
                <Fragment>
                    <Route path="" element={homeThing} />
                    <Route path="/home" element={<HomeFeed />} />
                    <Route path="/squadflix/*" element={<Cap17driveIndex squadflixTvHook={squadflixTvHook} />} />
                    {hasPermission("viewUsers") &&
                        <Route path="/admin-conf/users" element={<ChatbotUsers />} />}
                    <Route path="/chatbot/*" element={<ChatbotHome />} />
                    <Route path="/my-drive/*"
                        element={<MyDrive draggingItems={isDraggingItem}
                            toggleContextualMenu={toggleContextualMenu} />} />
                    <Route path="/groups" element={<Navigate to={'/my-profile/groups'} />}></Route>
                    <Route path="/groups/search-results" element={<GroupResults />} />
                    <Route path="/my-profile/*" element={<MyProfileView />} />
                    <Route path="/user/:userName/:code/*" element={<UserProfileDetails />} />
                    {isMobileDevice() && <Route path="/search-groups" element={<GroupSearcher />} />}
                    {hasPermission("viewSquadflixLogs") &&
                        <Route path="/squadflix-logs" element={<SquadflixLogsView />} />}
                    {auth.currentUser.emailVerified &&
                        <Fragment>
                            <Route path="/groups/new-group" element={<GroupForm />} />
                        </Fragment>}
                </Fragment>
            }
            <Route path="/shared-files/:id" element={<SharedFile />} />
            <Route path="/groups/group-details/:id" element={<GroupDetails />} />
            {auth.currentUser && <Route path="*" element={<MonkeyNotFound />} />}
            {!auth.currentUser && <Route path="*" element={<LoginPage />} />}
        </Routes>
    );

    const contextualRoutes = (
        <Routes>
            <Route path="/" element={<Cap17driveSearcher navigateToSearch onlyInput />} />
            <Route path="/home" element={<Cap17driveSearcher navigateToSearch onlyInput />} />
            <Route path="/my-drive/*" element={null} />
            {auth.currentUser && <Route path="/chatbot/*" element={<ChatbotSidebar />} />}
            {/*<Route path="squadflix/live/*" element={<SquadflixLiveSidebar/>}/>*/}
            <Route path="squadflix/*" element={<Cap17driveSearcher navigateToSearch onlyInput />} />
            <Route path="*" element={<Cap17driveSearcher navigateToSearch onlyInput />} />
        </Routes>
    );

    function draggingItem(ev) {
        ev.preventDefault();
        if (timeoutStopDragging) {
            clearTimeout(timeoutStopDragging);
            setTimeoutStopDragging(null);
        }
        setIsDraggingItem(true);
    }

    function leftDraggingItem(ev) {
        ev.preventDefault();
        if (!timeoutStopDragging) {
            setTimeoutStopDragging(setTimeout(() => {
                setIsDraggingItem(false);
            }, 200));
        }
    }

    function stopDraggingItem(ev) {
        ev.preventDefault();
        if (timeoutStopDragging) {
            clearTimeout(timeoutStopDragging);
            setTimeoutStopDragging(null);
        }
        setIsDraggingItem(false);
    }

    if (userPreferences?.tvMode) {
        return <TvBasicLayout squadflixTvHook={squadflixTvHook} />;
    }

    return (
        <div className="d-flex flex-row col-12 align-items-stretch"
            style={{ height: window.innerHeight }}
            onDragOver={draggingItem}
            onDrop={stopDraggingItem}
            onDragEnd={stopDraggingItem}
            onDragExit={leftDraggingItem}
            onDragLeave={leftDraggingItem}>
            {showTvPrompModal && <TvModePromptModal />}
            {!isMobileDevice() &&
                <div className="p-4 pe-3 squaddisk-home__aux-columns squaddisk-home__aux-columns--left" id="link-menu"
                    style={{
                        overflowX: "hidden",
                    }}>
                    <LinkMenu />
                </div>}
            <div
                className={"squaddisk-home__main-viewer  pt-4" + (isMobileDevice() ? " container-phone" : " pb-4 container")}
                id="home__scrollContainer"
                style={{
                    height: (isMobileDevice() && !isOpenInIframe) ? (window.innerHeight - 60 + 'px') : 'auto',
                    paddingBottom: isMobileDevice() ? ((isUploading || downloadingFile || squadflixTvHook.connectedReceptor) ? "95px" : "80px") : '0px',
                    "--scrollbar-display": userPreferences.showScrollbar ? "unset" : "none",
                }}>
                <DownloadQueue />
                {mainRoutes}
            </div>
            {!isMobileDevice() &&
                <div className="p-4 ps-3 squaddisk-home__aux-columns squaddisk-home__aux-columns--right"
                    id="contextual-menu" style={{}}>
                    {squadflixTvHook.connectedReceptor &&
                        <SquadflixTvControls isMobile={false} squadflixTvHook={squadflixTvHook} />}
                    <DriveContextualMenu />
                    {contextualRoutes}
                </div>}

            {isMobileDevice() && !isOpenInIframe && <div className="home__linKMenuPhone">
                <LinkMenu isOpen={isOpen} toggleContextualMenu={toggleContextualMenu} />
            </div>}

            {isMobileDevice() && isUploading &&
                <div style={{
                    position: "fixed",
                    zIndex: 2,
                    bottom: 75,
                    right: 15,
                    height: 'auto',
                    width: 'fit-content',
                    overflow: 'hidden'
                }} className="d-flex justify-content-end p-0 alert alert-info text-start col-8 mb-0">
                    <FileUploadMenu inMyDrive={false} onlyInfo={true}></FileUploadMenu>
                </div>
            }

            {isMobileDevice() && !isOpenInIframe && squadflixTvHook.connectedReceptor &&
                <div style={{ position: "fixed", zIndex: 2, bottom: 75, right: 15, left: 15, height: 'fit-content' }}>
                    <SquadflixTvControls isMobile={true} squadflixTvHook={squadflixTvHook} />
                </div>
            }
            <button style={{ display: 'none' }} type="button" data-bs-toggle="offcanvas" ref={buttonToggleContextual}
                data-bs-target="#offCanvasContextualMenu" aria-controls="offCanvasContextualMenu" />
            {isMobileDevice() && !isOpenInIframe &&
                <div className="offcanvas offcanvas-end" tabIndex="-1" id="offCanvasContextualMenu"
                    data-bs-backdrop="false"
                    aria-labelledby="offCanvasContextualMenuLabel"
                    style={{ height: isMobileDevice() ? (window.innerHeight - 60 + 'px') : '0px' }}>
                    <div className="offcanvas-header">
                        <h5 className="offcanvas-title" id="offCanvasContextualMenuLabel"><FormattedMessage
                            id="app.menu" /></h5>
                        <button type="button" className="btn-close" onClick={toggleContextualMenu}></button>
                    </div>
                    <div className="offcanvas-body">
                        <DriveContextualMenu />
                    </div>
                </div>}
            <SquadflixChatWidget />
        </div>
    );
};

export default Home;
